import { Member, MemberRole, MemberStatus } from '~/gen/proto/bff/v1/model_pb'
import { fromPbDate } from '~/utils/date'

import { EffectivePlan } from './effectivePlans'
import { assignedRoleCodes } from './filterMembers'
import { MemberCsvRow } from './memberCsv'

// Label resolvers injected by the caller (dict.role / dict.status) so this
// module stays testable without initializing i18n.
export type MemberCsvLabels = {
  role: (role: MemberRole) => string
  status: (status: MemberStatus) => string
}

// Build the per-plan role cells. Each cell holds the member's active role
// name(s) on that plan; multiple roles on the same plan are joined by ",".
// Members with no role on the plan get an empty cell.
//
// Caution: the "," separator is indistinguishable from a literal comma inside
// a role name. CSV quoting keeps the cell itself intact, but a downstream
// consumer cannot reliably split the cell back into individual role names if
// a role name ever contains a comma. Role names do not contain commas in
// practice today; revisit the separator if that assumption changes.
function buildPlanRoles(
  member: Member,
  plans: EffectivePlan[],
  isEnglish: boolean,
): string[] {
  return plans.map((plan) => {
    const codes = new Set(assignedRoleCodes(member, plan))
    return plan.roles
      .filter((role) => codes.has(role.code))
      .map((role) => (isEnglish ? role.engName : role.name))
      .join(',')
  })
}

// Convert a member into a CSV row with localized/plain string cells.
// `plans` defines the dynamic plan columns and must match the headers.
export function toMemberCsvRow(
  member: Member,
  plans: EffectivePlan[],
  isEnglish: boolean,
  labels: MemberCsvLabels,
): MemberCsvRow {
  return {
    name: member.name,
    email: member.email,
    organizationRole: labels.role(member.role),
    status: labels.status(member.status),
    createdAt: member.createdAt
      ? fromPbDate(member.createdAt).formatYYYYMMDD()
      : '',
    phoneNumber: member.phoneNumber,
    mobileNumber: member.mobileNumber,
    planRoles: buildPlanRoles(member, plans, isEnglish),
  }
}
